// @ts-strict-ignore
import { ContainerService } from '@3d-scene/model3d/container.service';
import { ArticlesCreator } from '../components/article/presenter/article-creator';
import { RangeOnLineConnectionUtils } from '@3d-scene/model3d/util/range-on-line-connection-utils';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { ArticleUsageInfoPropertyDef } from '@article/constants/article-usage-info-property-def';
import { VolatileDataSetHolder } from '../util/volatile-data-set.holder';
import { DrillingholePresetVO } from '../actions/drilling-hole/drilling-hole.vo';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { ArticleUsageVO, ArticleVO } from '@article/model/vo/article.vo';
import { DrillingHolePresetPresenter } from '@3d-scene/model3d/util/drillinghole/presenter/drilling-hole-preset.presenter';
import { UndoRedoManager } from '@shared/undo/undo-redo.manager';
import { UnitConverter } from '@3d-scene/common/util/unit-converter';
import { ProjectModel } from '../model/project.model';
import { DrillingHolePresetManager } from '../components/article/manager/drilling-hole-preset.manager';
import { ProjectUtils } from '../components/project/util/project-utils';
import { ConnectionAreaVO } from '@shared/vo/meta-element.vo';
import { ArticleUtils } from '@article/util/article-utils';
import { ArticleManager } from '@article/manager/article.manager';
import { Log } from '@shared/debug/log';
import { DrillingHoleService } from '@project/actions/drilling-hole/drilling-hole.service';
import { skipWhile, take } from 'rxjs/operators';
import { ProjectModelService } from '@project/service/project-model.service';
import { ConnectionInfoUtils } from '@3d-scene/model3d/util/connection-info-utils';
import { ArticleService } from '@article/service/article.service';
import { MountingRailCreationModel } from '@configuration/wizard/service/mounting-rail-creation.model';
import { MountingRailCreationService } from '@configuration/wizard/service/mounting-rail-creation.service';
import { CompositeArticleInfoUtils } from '@article/util/composite-article-info-utils';
import { Injectable } from '@angular/core';
import { UndoReplaceArticleInContainerOperation } from '@shared/undo/operation/undo-replace-article-in-container-operation';
import { PropertyVO } from '../components/article/model/vo/property.vo';
import { SelectionModelService } from '../components/article/service/selection-model.service';
import { ReplaceArticleService } from '@configuration/article/replace-article.service';

@Injectable({providedIn: 'root'})
export class ExchangeMountingRailModelProcessor {

    private mountingRailCreationModel: MountingRailCreationModel;
    private oldMountingRail: ArticleContainer;
    private newRail: ArticleVO;
    private oldRail: ArticleVO;
    private newRailLength: number;

    constructor(
      private drillingHolePresetPresenter: DrillingHolePresetPresenter,
      private undoRedoManager: UndoRedoManager,
      private projectModel: ProjectModel,
      private projectModelService: ProjectModelService,
      private drillingholePresetManager: DrillingHolePresetManager,
      private articleManager: ArticleManager,
      private compositeArticleInfoUtils: CompositeArticleInfoUtils,
      private containerService: ContainerService,
      private drillingHoleService: DrillingHoleService,
      private articleService: ArticleService,
      private mountingRailCreationService: MountingRailCreationService,
      private selectionModelService: SelectionModelService,
      private replaceArticleService: ReplaceArticleService,
    ) {
    }

    public async replaceSelectedCarrierRail(oldMountingRail: ArticleContainer, newRail: ArticleVO, newRailLength: number, mountingRailCreationModel: MountingRailCreationModel): Promise<ArticleVO[]> {

      this.oldMountingRail = oldMountingRail;
      this.oldRail = oldMountingRail.article;
      this.newRail = newRail;
      this.newRailLength = newRailLength;
      this.mountingRailCreationModel = mountingRailCreationModel;

      if (this.projectModel.currentProjectType) {
        const newProjectType = ProjectUtils.getProjectTypeByMountingRailArticle(this.newRail);
        if (this.projectModel.currentProjectType !== newProjectType) {
          this.projectModelService.currentProjectType = newProjectType;
        }
      }

      const unconnectableArticles = this.findUnconnectableArticles();
      if (unconnectableArticles.length > 0) {
        return unconnectableArticles;
      }

      this.projectModel.isMountingRailInProcess = true;

      await this.processMountingRail(oldMountingRail);

      this.projectModel.isMountingRailInProcess = false;

      return []
    }

  private async processMountingRail(articleContainer: ArticleContainer): Promise<void> {
      try {
        const originalDrillingHolePresetProperties: PropertyVO[]
          = this.drillingHolePresetPresenter.clearDrillingHolePresetProperties(articleContainer.articleUsage);
        const oldArticle = articleContainer.article;
        await this.replaceArticleService.replaceArticle(articleContainer, this.newRail);
        this.undoRedoManager.pushUndo(new UndoReplaceArticleInContainerOperation(this.replaceArticleService, articleContainer, oldArticle, this.newRail,
          originalDrillingHolePresetProperties, this.selectionModelService));
      } catch (error) {
        Log.error('Could not replace Article VO: ', error);
        return;
      }

      this.updateArticleUsage();

      try {
        await this.updateMountingRailData();
      } catch (error) {
        Log.error('Could not update mounting rail data: ', error);
        return;
      }

      try {
        await this.loadDrillingHoles();
      } catch (error) {
        Log.error('Could not load drilling holes: ', error);
      }
    }

    private findUnconnectableArticles(): ArticleVO[] {
        const unconnectableArticles: ArticleVO[] = [];

        const acceptingAreas: ConnectionAreaVO[] = ArticleUtils.getConnectionElements(this.newRail, ConnectionAreaVO.SIDE_ACCEPTING);
        const acceptingArea: ConnectionAreaVO = ConnectionInfoUtils.findRangeOnLineConnectionByType(acceptingAreas, null);
        const childGUIDs: string[] = ArticleUsageUtils.getAcceptingAreaIds(this.oldMountingRail.articleUsage);

        for (const guid of childGUIDs) {
          const childUsage: ArticleUsageVO = this.projectModel.getArticleUsage(guid);
          const childArticle: ArticleVO = this.articleManager.getCachedArticle(childUsage.articleId, childUsage.articleVersion);
          const requiredConnection: ConnectionAreaVO = ConnectionInfoUtils.findRequiredConnection(childArticle, this.newRail, acceptingArea);
          if (!requiredConnection && !this.articleService.isArticleWithoutFeatureProperties(childArticle)) {
            if (ArticleUsageUtils.isCompositePartArticleUsage(childUsage) ) {
                unconnectableArticles.push(this.compositeArticleInfoUtils.getCompositeArticleFromUsageByArticleNo(childUsage));
            } else {
                unconnectableArticles.push(childArticle);
            }
          }
        }
        return unconnectableArticles;
    }

    private updateArticleUsage(): void {
      const railContainer = this.containerService.findArticleContainerInGlobal(this.oldMountingRail.articleUsage.id);
      const railWasStretched: boolean = ArticlesCreator.updateRangeOnLineConnection(railContainer, railContainer.article, RangeOnLineConnectionUtils.STRETCH_LENGTH_IN_AWAY_UNITS);
      if (railWasStretched) {
        ArticleUsageUtils.setArticleUsageInfoPropertyBooleanValue(this.mountingRailCreationService.getMountingRailArticleUsage(null, this.mountingRailCreationModel), ArticleUsageInfoPropertyDef.IS_STRETCHED, true);
        ArticleUsageUtils.setArticleUsageInfoPropertyFloatValue(
          this.mountingRailCreationService.getMountingRailArticleUsage(null, this.mountingRailCreationModel),
          ArticleUsageInfoPropertyDef.STRETCH_LENGTH_ON_BOTH_SIDES,
          RangeOnLineConnectionUtils.STRETCH_LENGTH_IN_AWAY_UNITS_ON_BOTH_SIDES
        );
      } else {
        ArticleUsageUtils.removeArticleUsageInfoProperty(this.mountingRailCreationService.getMountingRailArticleUsage(null, this.mountingRailCreationModel), ArticleUsageInfoPropertyDef.IS_STRETCHED);
        ArticleUsageUtils.removeArticleUsageInfoProperty(this.mountingRailCreationService.getMountingRailArticleUsage(null, this.mountingRailCreationModel), ArticleUsageInfoPropertyDef.STRETCH_LENGTH_ON_BOTH_SIDES);
      }
    }

    private async updateMountingRailData(): Promise<void> {
      // set values in presenter in away3D dimension
      this.mountingRailCreationModel.mountingRailLength = this.newRailLength;
      this.mountingRailCreationModel.startBlockingLength = 0;
      this.mountingRailCreationModel.endBlockingLength = 0;
      this.mountingRailCreationModel.drillingHoleOffset = 0;

      VolatileDataSetHolder.instance.setDataSetValue(VolatileDataSetHolder.DATA_SET_DRILLINGHOLEWIZARD,
          VolatileDataSetHolder.DATA_SET_CACHED_DRILLINGHOLE_OFFSET_KEY, this.mountingRailCreationModel.drillingHoleOffset);
      // openSide is instantly updated
      await this.mountingRailCreationService.startLoadingMountingRailWithArticleContainerAttached(this.newRail, this.mountingRailCreationModel);
    }

    private async loadDrillingHoles(): Promise<void> {
      let preset: DrillingholePresetVO;
      try {
         preset = await this.drillingholePresetManager.getDrillingHolePresetByRailId(this.newRail.identity).toPromise();
      } catch (error) {
        Log.error(`Could not load drilling holes preset: `, error);
        return;
      }

      try {
        await this.processDrillingHoles(preset);
      } catch (error) {
        Log.error(`Could not process drilling holes: `, error);
      }
    }


    private async processDrillingHoles(drillingHolePreset: DrillingholePresetVO): Promise<void> {
      if (!drillingHolePreset) {
        return;
      }

      VolatileDataSetHolder.instance.setDataSetValue(VolatileDataSetHolder.DATA_SET_DRILLINGHOLEWIZARD,
          VolatileDataSetHolder.DATA_SET_DRILLINGHOLEPRESET_KEY, drillingHolePreset);
      if (this.mountingRailCreationModel.updateDrillingHoles) {
          const delta: number = this.mountingRailCreationModel.drillingHoleOffset - this.mountingRailCreationModel.existingDrillingHoleOffset;
          await this.drillingHolePresetPresenter.updateDrillingHoles(this.mountingRailCreationService.getMountingRailArticleUsage(this.newRail, this.mountingRailCreationModel),
          this.mountingRailCreationModel.mountingRailLength, 0, 0, this.mountingRailCreationModel.drillingHoleOffset, delta, drillingHolePreset, false);
      } else {
        let recreateDrillingHoles: boolean = false;
        if (this.newRail && this.oldRail && this.newRail.articleNo !== this.oldRail.articleNo) {
            recreateDrillingHoles = true; // mounting rail was changed
        } else if (!this.mountingRailCreationModel.existingMountingRail) {
            recreateDrillingHoles = true; // we start new project, so there no drilling holes yet, so drilling holes need to be created
        } else {
            // do not (re)create drilling holes, nothing was changed that effected drilling holes
            // if something in this regards was changed, then it would be dealt with in update drilling holes
        }
        if (recreateDrillingHoles) {
          const completedDrillingHolesProcess = new Promise<void>((resolve) => {
            this.drillingHoleService.drillingHoleNotification$.pipe(
              skipWhile(notification => notification.action !== this.drillingHoleService.ACTION_NOTIFY_PRESET_COMPLETED || !notification.success),
              take(1))
            .subscribe(() => resolve())
          });
          const pushUndoOperations: boolean = this.newRail ? true : false;
          const mountingRailArticleUsage: ArticleUsageVO = this.mountingRailCreationService.getMountingRailArticleUsage(this.newRail, this.mountingRailCreationModel);
          const railLength = UnitConverter.fromTo('3d', 'mm', this.newRailLength);
          await this.drillingHolePresetPresenter.createDrillingHolePreset(railLength, this.mountingRailCreationModel.drillingHoleOffset,
            drillingHolePreset, mountingRailArticleUsage, pushUndoOperations);

          await completedDrillingHolesProcess;
        }
      }
    }
}
